import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Sync,
  Refresh,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  Business,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adAccountsService, AdAccount, FbAdAccount } from '../../services/ad-accounts.service';
import { syncService } from '../../services/sync.service';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import { useAuthStore } from '../../stores/auth.store';

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  pending: 'warning',
  disconnected: 'error',
  error: 'error',
};

const accountStatusLabels: Record<number, { label: string; color: 'success' | 'warning' | 'error' }> = {
  1: { label: 'Active', color: 'success' },
  2: { label: 'Disabled', color: 'error' },
  3: { label: 'Unsettled', color: 'warning' },
  7: { label: 'Pending Review', color: 'warning' },
  9: { label: 'In Grace Period', color: 'warning' },
  100: { label: 'Pending Closure', color: 'error' },
  101: { label: 'Closed', color: 'error' },
};

type ConnectStep = 'token' | 'manual-ids' | 'select';

export default function AdAccountsPage() {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAccount, setMenuAccount] = useState<AdAccount | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [syncingAccount, setSyncingAccount] = useState<string | null>(null);

  // Connect dialog state
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connectStep, setConnectStep] = useState<ConnectStep>('token');
  const [tokenInput, setTokenInput] = useState('');
  const [accountIdsInput, setAccountIdsInput] = useState('');
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<FbAdAccount[]>([]);
  const [tempToken, setTempToken] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [reuseSourceId, setReuseSourceId] = useState<string>('');
  const [connectWorkspaceId, setConnectWorkspaceId] = useState(defaultCreateWorkspace());
  const multiWorkspace = useAuthStore((s) => (s.user?.workspaces?.length ?? 0) > 1);

  const openConnectDialog = () => {
    setConnectWorkspaceId(defaultCreateWorkspace());
    setConnectDialogOpen(true);
  };

  const { data: accountsResult, isLoading, error } = useQuery({
    queryKey: ['adAccounts'],
    queryFn: () => adAccountsService.query(),
  });

  const handleValidationSuccess = (result: {
    needsManualAccountIds?: boolean;
    accounts: FbAdAccount[];
    token: string;
  }) => {
    if (result.needsManualAccountIds && result.accounts.length === 0) {
      setTempToken(result.token);
      setConnectStep('manual-ids');
      setTokenError(null);
      return;
    }
    setAvailableAccounts(result.accounts);
    setTempToken(result.token);
    setConnectStep('select');
    setTokenError(null);
  };

  const validateMutation = useMutation({
    mutationFn: ({ accessToken, adAccountIds }: { accessToken: string; adAccountIds?: string[] }) =>
      adAccountsService.validateToken(accessToken, connectWorkspaceId, adAccountIds),
    onSuccess: handleValidationSuccess,
    onError: (err: Error) => {
      setTokenError(err.message || 'Invalid token. Please check and try again.');
    },
  });

  const reuseMutation = useMutation({
    mutationFn: ({ sourceAdAccountId, adAccountIds }: { sourceAdAccountId: string; adAccountIds?: string[] }) =>
      adAccountsService.reuseExistingToken(sourceAdAccountId, connectWorkspaceId, adAccountIds),
    onSuccess: handleValidationSuccess,
    onError: (err: Error) => {
      setTokenError(err.message || 'Failed to reuse existing token.');
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const selected = availableAccounts.filter((a) => selectedAccounts.has(a.id));
      return adAccountsService.saveAdAccounts({
        token: tempToken,
        accounts: selected.map((account) => ({
          accountId: account.id,
          accountName: account.name,
          currency: account.currency,
          timezone: account.timezone_name,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adAccounts'] });
      handleCloseConnectDialog();
    },
    onError: (err: Error) => {
      setTokenError(err.message || 'Failed to save ad accounts.');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: adAccountsService.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adAccounts'] });
      setDeleteDialogOpen(false);
      setMenuAccount(null);
    },
  });

  const syncMutation = useMutation({
    mutationFn: adAccountsService.triggerSync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adAccounts'] });
      setSyncingAccount(null);
    },
    onError: () => {
      setSyncingAccount(null);
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async (accountId: string) => {
      await syncService.pullState(accountId);
      await syncService.syncInsights(accountId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-time-series'] });
      setSyncingAccount(null);
    },
    onError: () => {
      setSyncingAccount(null);
    },
  });

  const handleSync = (accountId: string) => {
    setSyncingAccount(accountId);
    syncMutation.mutate(accountId);
    setAnchorEl(null);
  };

  const handleRefresh = (accountId: string) => {
    setSyncingAccount(accountId);
    refreshMutation.mutate(accountId);
    setAnchorEl(null);
  };

  const handleCloseConnectDialog = () => {
    setConnectDialogOpen(false);
    setConnectStep('token');
    setTokenInput('');
    setAccountIdsInput('');
    setTokenError(null);
    setAvailableAccounts([]);
    setTempToken('');
    setSelectedAccounts(new Set());
    setReuseSourceId('');
  };

  const toggleAccount = (accountId: string) => {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Account Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fb_account_id',
      headerName: 'Facebook ID',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 100,
    },
    {
      field: 'timezone',
      headerName: 'Timezone',
      width: 180,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColors[params.value?.toLowerCase()] || 'default'}
          icon={params.value === 'active' ? <CheckCircle /> : params.value === 'error' ? <ErrorIcon /> : undefined}
        />
      ),
    },
    {
      field: 'last_synced_at',
      headerName: 'Last Synced',
      width: 160,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString() : 'Never',
    },
    {
      field: 'created_at',
      headerName: 'Connected',
      width: 120,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuAccount(params.row);
          }}
        >
          {syncingAccount === params.row.id ? (
            <CircularProgress size={20} />
          ) : (
            <MoreVert />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Ad Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openConnectDialog}
        >
          Connect Ad Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load ad accounts. Please try again.
        </Alert>
      )}

      {accountsResult?.data?.length === 0 && !isLoading ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No Ad Accounts Connected
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Connect your Facebook Ad Accounts to start managing campaigns.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openConnectDialog}
            >
              Connect Ad Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <DataGridPremium
          rows={accountsResult?.data || []}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[50, 100, 200, 400]}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAccount) {
              handleRefresh(menuAccount.id);
            }
          }}
          disabled={refreshMutation.isPending || syncMutation.isPending}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          Refresh Data
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAccount) {
              handleSync(menuAccount.id);
            }
          }}
          disabled={syncMutation.isPending || refreshMutation.isPending}
        >
          <ListItemIcon>
            <Sync fontSize="small" />
          </ListItemIcon>
          Push Changes
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Disconnect
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Disconnect Ad Account?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect "{menuAccount?.name}"? This will remove access to this ad account and all associated campaigns.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => menuAccount && disconnectMutation.mutate(menuAccount.id)}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connect Ad Account Dialog */}
      <Dialog
        open={connectDialogOpen}
        onClose={handleCloseConnectDialog}
        maxWidth={connectStep === 'select' || connectStep === 'manual-ids' ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          {connectStep === 'token' ? 'Connect Ad Account' : connectStep === 'manual-ids' ? 'Enter Ad Account IDs' : 'Select Ad Accounts'}
        </DialogTitle>
        <DialogContent>
          {tokenError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tokenError}
            </Alert>
          )}

          {connectStep === 'token' && (
            <>
              {multiWorkspace && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Workspace</Typography>
                  <WorkspaceField value={connectWorkspaceId} onChange={setConnectWorkspaceId} />
                  <Typography variant="caption" color="text.secondary">
                    The connected ad account(s) are added to this workspace.
                  </Typography>
                </Box>
              )}
              {(accountsResult?.data?.length ?? 0) > 0 && (
                <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reuse token from an existing account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Skip re-pasting the token if you want to connect another ad account using the same System User.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel id="reuse-source-label">Source ad account</InputLabel>
                      <Select
                        labelId="reuse-source-label"
                        label="Source ad account"
                        value={reuseSourceId}
                        onChange={(e) => setReuseSourceId(e.target.value as string)}
                        disabled={reuseMutation.isPending}
                      >
                        {accountsResult?.data.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.name} (act_{a.fb_account_id})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={() => reuseMutation.mutate({ sourceAdAccountId: reuseSourceId })}
                      disabled={!reuseSourceId || !connectWorkspaceId || reuseMutation.isPending}
                      startIcon={reuseMutation.isPending ? <CircularProgress size={18} /> : undefined}
                    >
                      {reuseMutation.isPending ? 'Loading...' : 'Use this token'}
                    </Button>
                  </Box>
                </Box>
              )}

              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Or paste a new Facebook System User Access Token. You can generate one in{' '}
                <Typography component="span" color="primary" sx={{ cursor: 'pointer' }}>
                  Facebook Business Manager &gt; Business Settings &gt; System Users
                </Typography>.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="System User Access Token"
                placeholder="Paste your access token here"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                multiline
                minRows={3}
                maxRows={5}
                disabled={validateMutation.isPending}
              />
            </>
          )}

          {connectStep === 'manual-ids' && (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Auto-discovery is not available for this token. Enter the Ad Account IDs you want to connect (comma-separated). Find them in Facebook Business Manager → Ad Accounts.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Ad Account IDs"
                placeholder="e.g. act_123456789, act_987654321"
                value={accountIdsInput}
                onChange={(e) => setAccountIdsInput(e.target.value)}
                disabled={validateMutation.isPending}
                helperText="Enter one or more Ad Account IDs separated by commas"
              />
            </>
          )}

          {connectStep === 'select' && (
            <>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Select the ad accounts you want to connect.
              </Typography>
              <Grid container spacing={2}>
                {availableAccounts.map((account) => {
                  const status = accountStatusLabels[account.account_status] || {
                    label: 'Unknown',
                    color: 'warning' as const,
                  };
                  const isSelected = selectedAccounts.has(account.id);

                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={account.id}>
                      <Card
                        sx={{
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          position: 'relative',
                        }}
                      >
                        <CardActionArea onClick={() => toggleAccount(account.id)}>
                          <CardContent>
                            {isSelected && (
                              <CheckCircle
                                color="primary"
                                sx={{ position: 'absolute', top: 12, right: 12 }}
                              />
                            )}
                            <Typography variant="h6" gutterBottom>
                              {account.name}
                            </Typography>
                            {account.business_name && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Business fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  {account.business_name}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              ID: {account.account_id}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={account.currency} size="small" variant="outlined" />
                              <Chip label={status.label} size="small" color={status.color} />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {selectedAccounts.size > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {selectedAccounts.size} account{selectedAccounts.size > 1 ? 's' : ''} selected
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConnectDialog}>Cancel</Button>
          {connectStep === 'token' && (
            <Button
              variant="contained"
              onClick={() => validateMutation.mutate({ accessToken: tokenInput })}
              disabled={!tokenInput.trim() || !connectWorkspaceId || validateMutation.isPending}
              startIcon={validateMutation.isPending ? <CircularProgress size={20} /> : undefined}
            >
              {validateMutation.isPending ? 'Validating...' : 'Validate'}
            </Button>
          )}
          {connectStep === 'manual-ids' && (
            <>
              <Button onClick={() => setConnectStep('token')}>Back</Button>
              <Button
                variant="contained"
                onClick={() => {
                  const ids = accountIdsInput.split(',').map((id) => id.trim()).filter(Boolean);
                  if (ids.length > 0) {
                    validateMutation.mutate({ accessToken: tokenInput, adAccountIds: ids });
                  }
                }}
                disabled={!accountIdsInput.trim() || !connectWorkspaceId || validateMutation.isPending}
                startIcon={validateMutation.isPending ? <CircularProgress size={20} /> : undefined}
              >
                {validateMutation.isPending ? 'Fetching...' : 'Fetch Accounts'}
              </Button>
            </>
          )}
          {connectStep === 'select' && (
            <>
              <Button onClick={() => setConnectStep('token')}>Back</Button>
              <Button
                variant="contained"
                disabled={selectedAccounts.size === 0 || saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
                startIcon={saveMutation.isPending ? <CircularProgress size={20} /> : undefined}
              >
                {saveMutation.isPending
                  ? 'Connecting...'
                  : `Connect ${selectedAccounts.size > 1 ? `${selectedAccounts.size} Accounts` : 'Account'}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
